/**
 * API Service
 * Handles all API calls to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

/**
 * Fetch all products from the API
 * @returns {Promise<Array>} Array of products
 */
export const fetchProducts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/products?populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

/**
 * Fetch featured products from the API
 * @returns {Promise<Array>} Array of featured products
 */
export const fetchFeaturedProducts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/products?filters[featured][$eq]=true&populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw error;
  }
};

/**
 * Fetch product by slug
 * @param {string} slug - Product slug
 * @returns {Promise<Object>} Product object
 */
export const fetchProductBySlug = async (slug) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/products?filters[slug][$eq]=${slug}&populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    console.error('Error fetching product by slug:', error);
    throw error;
  }
};

/**
 * Fetch product categories from the API
 * @returns {Promise<Array>} Array of categories
 */
export const fetchCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/product-categories?populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Fetch products by category
 * @param {string} categoryId - Category ID
 * @returns {Promise<Array>} Array of products in the category
 */
export const fetchProductsByCategory = async (categoryId) => {
  try {
    // Fetch all products with populated relations
    const response = await fetch(`${API_BASE_URL}/api/products?populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    const allProducts = result.data || [];
    
    // Filter products by category ID client-side
    // Strapi v5 has flat structure (no attributes wrapper)
    const filteredProducts = allProducts.filter(product => {
      const categoryData = product.product_category;
      
      if (!categoryData) {
        return false;
      }
      
      // Handle both single category and array of categories
      if (Array.isArray(categoryData)) {
        return categoryData.some(cat => cat.id === parseInt(categoryId));
      }
      
      // Single category - check if ID matches
      return categoryData.id === parseInt(categoryId);
    });
    
    return filteredProducts;
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw error;
  }
};

/**
 * Helper function to get image URL from Strapi image object or array
 * @param {Object|Array} imageData - Strapi image data object or array of image objects
 * @param {string} size - Image size (thumbnail, small, medium, large, or original)
 * @returns {string|null} Full image URL or null if not available
 */
export const getImageUrl = (imageData, size = 'medium') => {
  if (!imageData) return null;
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';
  
  // If imageData is an array, get the first image
  if (Array.isArray(imageData)) {
    if (imageData.length === 0) return null;
    imageData = imageData[0]; // Use first image in array
  }
  
  // If imageData is already a string URL, return it
  if (typeof imageData === 'string') {
    return imageData.startsWith('http') ? imageData : `${API_URL}${imageData}`;
  }
  
  // Handle Strapi image object structure
  let imageUrl = null;
  
  // Try to get the requested size from formats
  if (imageData.formats && imageData.formats[size]) {
    imageUrl = imageData.formats[size].url;
  } 
  // Fallback to original image URL
  else if (imageData.url) {
    imageUrl = imageData.url;
  }
  
  // Return full URL
  return imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
};

/**
 * Helper function to get all image URLs from Strapi image array
 * Useful for image galleries or multiple product images
 * @param {Array} imageData - Strapi image data array
 * @param {string} size - Image size (thumbnail, small, medium, large, or original)
 * @returns {Array<string>} Array of full image URLs
 */
export const getAllImageUrls = (imageData, size = 'medium') => {
  if (!imageData || !Array.isArray(imageData)) return [];
  
  return imageData
    .map(img => getImageUrl(img, size))
    .filter(url => url !== null);
};

/**
 * Fetch all solutions from the API
 * @returns {Promise<Array>} Array of solutions
 */
export const fetchSolutions = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/solutions?populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching solutions:', error);
    throw error;
  }
};

/**
 * Fetch solution by slug
 * @param {string} slug - Solution slug
 * @returns {Promise<Object>} Solution object
 */
export const fetchSolutionBySlug = async (slug) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/solutions?filters[slug][$eq]=${slug}&populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    console.error('Error fetching solution by slug:', error);
    throw error;
  }
};

/**
 * Submit contact form data
 * @param {Object} contactData - Contact form data
 * @param {string} contactData.Full_Name - Full name
 * @param {string} contactData.Email_Address - Email address
 * @param {string} contactData.Phone_Number - Phone number
 * @param {string} contactData.Subject - Subject
 * @param {string} contactData.Description - Message/Description
 * @returns {Promise<Object>} Response from the API
 */
export const submitContactForm = async (contactData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: contactData
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};

/**
 * Fetch all blog posts from the API
 * @param {number} pageSize - Number of posts per page (default: 10)
 * @param {number} pageNumber - Page number (default: 1)
 * @returns {Promise<Object>} Object with data array and pagination info
 * Fetches: title, description, slug, createdAt, and when available: cover image, author, category, featured flag, blocks
 */
export const fetchBlogs = async (pageSize = 10, pageNumber = 1) => {
  try {
    const start = (pageNumber - 1) * pageSize;
    // populate=* fetches all relations including cover, blocks, author, category, etc.
    const response = await fetch(
      `${API_BASE_URL}/api/articles?pagination[start]=${start}&pagination[limit]=${pageSize}&populate=*&sort=createdAt:desc`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return {
      data: result.data || [],
      pagination: result.meta?.pagination || { pageSize, page: pageNumber, total: 0 }
    };
  } catch (error) {
    console.error('Error fetching blogs:', error);
    throw error;
  }
};

/**
 * Fetch featured blog posts from the API
 * @param {number} limit - Number of featured posts to fetch (default: 3)
 * @returns {Promise<Array>} Array of featured blog posts
 * Fetches: Only articles with featured=true, includes cover image, blocks, author, category when available
 */
export const fetchFeaturedBlogs = async (limit = 3) => {
  try {
    // populate=* fetches all relations: cover images, dynamic blocks, author, category relationships
    const response = await fetch(
      `${API_BASE_URL}/api/articles?filters[featured][$eq]=true&pagination[start]=0&pagination[limit]=${limit}&populate=*&sort=createdAt:desc`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching featured blogs:', error);
    throw error;
  }
};

/**
 * Fetch blog post by slug
 * @param {string} slug - Blog slug
 * @returns {Promise<Object>} Blog post object
 * Fetches: Full article with all relations including cover image, dynamic blocks, author, category
 */
export const fetchBlogBySlug = async (slug) => {
  try {
    // populate=* fetches all relations including:
    // - cover: Featured cover image
    // - blocks: Dynamic content blocks (rich-text, media, quotes, sliders, files)
    // - author: Author information
    // - category: Article category
    const response = await fetch(
      `${API_BASE_URL}/api/articles?filters[slug][$eq]=${slug}&populate=*`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    console.error('Error fetching blog by slug:', error);
    throw error;
  }
};

/**
 * Fetch blog posts by category
 * @param {number} categoryId - Category ID
 * @param {number} pageSize - Number of posts per page (default: 10)
 * @param {number} pageNumber - Page number (default: 1)
 * @returns {Promise<Object>} Object with data array and pagination info
 * Fetches: Articles in a specific category with all relations (cover, blocks, author)
 */
export const fetchBlogsByCategory = async (categoryId, pageSize = 10, pageNumber = 1) => {
  try {
    const start = (pageNumber - 1) * pageSize;
    // populate=* fetches all relations for category-filtered articles
    const response = await fetch(
      `${API_BASE_URL}/api/articles?filters[category][id][$eq]=${categoryId}&pagination[start]=${start}&pagination[limit]=${pageSize}&populate=*&sort=createdAt:desc`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return {
      data: result.data || [],
      pagination: result.meta?.pagination || { pageSize, page: pageNumber, total: 0 }
    };
  } catch (error) {
    console.error('Error fetching blogs by category:', error);
    throw error;
  }
};

/**
 * Fetch article categories from the API
 * @returns {Promise<Array>} Array of article categories
 */
export const fetchBlogCategories = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/article-categories?populate=*`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching article categories:', error);
    throw error;
  }
};